/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    EVENT_HTTP_SERVICE_START,
    EVENT_HTTP_SERVICE_STOP,
    IEventHttpServiceStopProperties,
    InternalTelemetry,
    StaticTelemetry
} from '@msdyn365-commerce/telemetry-internal';
import axios, { AxiosRequestConfig } from 'axios';
import { HTTPMethod, IHTTPError, IHTTPRequestOptions, IHTTPResponse, IODataResponse, IRequestContext } from '../interfaces';
import msdyn365Commerce from '../msdyn365-commerce';

// Default timeout is 10 seconds
const defaultTimeout = 10000;

/**
 * Sets retail server specific headers needed for auth
 * @param requestConfig The current configuration settings of the request
 * @param apiSettings The retail server API configuration settings
 */
const setRetailServerRequestHeaders = (requestConfig: AxiosRequestConfig) => {
    if (!requestConfig.headers) {
        requestConfig.headers = {};
    }

    requestConfig.headers['from-keystone'] = 'true';
    requestConfig.headers.oun = requestConfig.headers.oun || msdyn365Commerce.apiSettings.oun || '';
    requestConfig.headers.Accept = requestConfig.headers.Accept || 'application/json;odata=nometadata';
    requestConfig.headers.ContentType = requestConfig.headers.ContentType || 'application/json;odata=verbose';

    return requestConfig;
};

/**
 * Parses an OData Response and returns just the return type of the call without the extra OData Metadata
 */
const parseODataResponse = <T>(response?: IODataResponse<T>): T => {
    if (response && response.value && response['@odata.context']) {
        return response.value;
    } else {
        return <T>(<unknown>response);
    }
};

/**
 * Creates a basic Axios Request Config that is shared across commerce and standard HTTP calls
 * @param requestUrl The url of the request
 * @param httpMethod The HTTP method of the request
 * @param body The body content of the request
 * @param options The options associated with the request
 */
const createDefaultRequest = (
    requestUrl: string,
    httpMethod?: HTTPMethod,
    body?: unknown,
    options?: IHTTPRequestOptions
): AxiosRequestConfig => {
    return {
        method: httpMethod || 'get',
        url: requestUrl,
        data: body,
        timeout: options ? options.timeout || defaultTimeout : defaultTimeout,
        headers: options ? options.headers || {} : {}
    };
};

/**
 * Send an retail server request
 * @param requestUrl The url of the request
 * @param httpMethod The HTTP method of the request
 * @param body The body content of the request
 * @param options The options associated with the request
 */
export const sendCommerceRequest = async <T>(
    requestUrl: string,
    httpMethod?: HTTPMethod,
    body?: unknown,
    options?: IHTTPRequestOptions
): Promise<IHTTPResponse<T>> => {
    let request = createDefaultRequest(requestUrl, httpMethod, body, options);
    const requestId = InternalTelemetry.generateGuid();

    // Add pre-call telemetry here
    StaticTelemetry.trackEvent(EVENT_HTTP_SERVICE_START, { id: requestId });

    // Add the retail server request headers
    // Ideally these will be pulled via our zone implementation so they can be completely hidden from the end user
    request = setRetailServerRequestHeaders(request);

    // Send the request
    return axios
        .request(request)
        .then((response: IHTTPResponse<IODataResponse<T>>) => {
            // Add any post-call telemetry here
            StaticTelemetry.trackEvent<IEventHttpServiceStopProperties, {}>(EVENT_HTTP_SERVICE_STOP, {
                statusCode: `${response.status}`,
                success: 'true',
                id: requestId
            });

            // Parse OData and return response
            return {
                ...response,
                data: parseODataResponse(response.data)
            };
        })
        .catch((error: IHTTPError) => {
            // Add error handling telemetry and response here
            StaticTelemetry.trackEvent<IEventHttpServiceStopProperties, {}>(EVENT_HTTP_SERVICE_STOP, {
                statusCode: `${error.code}`,
                success: 'false',
                id: requestId
            });

            // Return either passed error response or empty response to user
            return error.response || <IHTTPResponse>{};
        });
};

/**
 * Send an external HTTP request
 * @param requestUrl The url of the request
 * @param httpMethod The HTTP method of the request
 * @param body The body content of the request
 * @param options The options associated with the request
 */
export const sendRequest = async <T>(
    requestUrl: string,
    httpMethod?: HTTPMethod,
    body?: unknown,
    options?: IHTTPRequestOptions
): Promise<IHTTPResponse<T>> => {
    const request = createDefaultRequest(requestUrl, httpMethod, body, options);
    const requestId = InternalTelemetry.generateGuid();

    // Add pre-call telemetry here
    StaticTelemetry.trackEvent(EVENT_HTTP_SERVICE_START, { id: requestId });

    // Send the request
    return _sendRequest(requestUrl, httpMethod, body, options, request, requestId);
};

/**
 * Commerce API request util method
 * @param requestContext request context object available in module props
 * @param dataActionId id of the data action that needs to be run on server side
 * @param httpMethod http method object - default is GET
 * @param body request body in case of POST
 * @param options http request options
 */
export const commerceApiRequest = async <T>(
    requestContext: IRequestContext,
    dataActionId: string,
    httpMethod?: HTTPMethod,
    body?: unknown,
    options?: IHTTPRequestOptions
): Promise<IHTTPResponse<T>> => {
    const requestId = requestContext.operationId;
    options = {
        headers: {
            'Content-Type': 'application/json',
            'Request-Id': requestContext.operationId
        },
        ...options
    };
    const requestUrl =
        `${process.env.NODE_ENV === 'development' ? '/_msdyn365/' : `${requestContext.sitePath}/_msdyn365/`}` + `api?id=${dataActionId}`;
    const request = createDefaultRequest(requestUrl, httpMethod || 'get', body, options);

    return _sendRequest(requestUrl, httpMethod, body, options, request, requestId);
};

/**
 * Commerce video api request util method
 * @param requestContext request context object available in module props
 * @param identities list of video identities
 * @param eCommerceOverrideChannelId override ecommerce channelId that can be used for cross-channel scenarios. If not passed, will fallback to eCommerceChannelId from requestContext
 */
export const commerceVideoMetadataRequest = async <T>(
    requestContext: IRequestContext,
    identities: string[],
    eCommerceOverrideChannelId?: string
): Promise<IHTTPResponse<T>> => {
    if (!identities || identities.length === 0) {
        throw new Error('Invalid input: atleast one identity should be specified');
    }

    if (!requestContext.operationId) {
        throw new Error('Missing operation id');
    }

    if (!(requestContext.apiSettings && requestContext.apiSettings.eCommerceChannelId) && !eCommerceOverrideChannelId) {
        throw new Error('Missing ecommerce channel id and no override channelId provided');
    }

    const requestId = requestContext.operationId;
    const options: IHTTPRequestOptions = {
        headers: {
            'Content-Type': 'application/json',
            'Request-Id': requestContext.operationId,
            eCommerceChannelId: eCommerceOverrideChannelId || requestContext.apiSettings.eCommerceChannelId,
            videoIdentities: identities.join(','),
            locale: requestContext.locale
        }
    };

    const domainName = requestContext.url.requestUrl.origin;
    const requestPath = '/_msdyn365/api/videofiledata';
    const requestUrl = process.env.NODE_ENV === 'development' || !domainName ? requestPath : `${domainName}${requestPath}`;

    const request = createDefaultRequest(requestUrl, 'post', {}, options);

    return _sendRequest(requestUrl, 'post', {}, options, request, requestId);
};

const _sendRequest = async <T>(
    requestUrl: string,
    httpMethod?: HTTPMethod,
    body?: unknown,
    options?: IHTTPRequestOptions,
    requestConfig?: AxiosRequestConfig,
    operationId?: string
): Promise<IHTTPResponse<T>> => {
    const request = requestConfig || createDefaultRequest(requestUrl, httpMethod, body, options);
    const requestId = operationId || InternalTelemetry.generateGuid();

    // Add pre-call telemetry here
    StaticTelemetry.trackEvent(EVENT_HTTP_SERVICE_START, { id: requestId });

    // Send the request
    return axios
        .request(request)
        .then((response: IHTTPResponse<T>) => {
            // Add any post-call telemetry here
            StaticTelemetry.trackEvent<IEventHttpServiceStopProperties, {}>(EVENT_HTTP_SERVICE_STOP, {
                statusCode: `${response.status}`,
                success: 'true',
                id: requestId
            });

            // Return response
            return response;
        })
        .catch((error: IHTTPError) => {
            StaticTelemetry.trackEvent<IEventHttpServiceStopProperties, {}>(EVENT_HTTP_SERVICE_STOP, {
                statusCode: `${error.code}`,
                success: 'false',
                id: requestId
            });

            // Return either passed error response or empty response to user
            return error.response || <IHTTPResponse>{};
        });
};
